/**
 * SupportChatFab — floating live-support chat button + panel (iter186).
 * Replaces the older MessengerFab. AI-first: the user talks to a Claude
 * Haiku 4.5 assistant that knows iStudyPro; on escalation, we capture
 * the user's email + transcript and open a real support ticket via the
 * existing /support helpdesk pipeline.
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { LifeBuoy, Send, X, User, Bot, AlertCircle, CheckCircle2, Sparkles, BookOpen, ChevronLeft } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'isp_support_chat_session_v1';

const SupportChatFab = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem(SESSION_KEY) || null; } catch { return null; }
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [needsEscalation, setNeedsEscalation] = useState(false);
  const [escalationEmail, setEscalationEmail] = useState(user?.email || '');
  const [escalationSubmitting, setEscalationSubmitting] = useState(false);
  const [ticketCreated, setTicketCreated] = useState(null); // ticket_id once created
  const [error, setError] = useState(null);
  // Tracks whether the backend has personalised the system prompt for
  // this user (their tier, recent lectures, billing status, etc.). When
  // true we surface a tiny "Trained on your usage" badge in the header
  // so the user knows they'll get diagnostic answers.
  const [personalized, setPersonalized] = useState(false);
  // "Common issues this week" chips — fetched once per panel open from
  // /support/chat/suggestions. Each chip carries a question string we
  // pre-populate + auto-send when clicked. iter188 — most support
  // questions are pattern-matched, so this lifts conversions.
  const [chips, setChips] = useState([]);
  // Personal-knowledge-base view (iter189) — list of the user's past
  // chat sessions, surfaced via a special "Saved my last N questions"
  // chip. Tapping a session loads its transcript into the main view
  // (read-only); a Back button returns to the active chat.
  const [history, setHistory] = useState([]);
  const [view, setView] = useState('chat'); // 'chat' | 'history' | 'archive'
  const [archived, setArchived] = useState(null); // {session_id, messages, ...}
  const scrollRef = useRef(null);

  // Seed the welcome line whenever the panel opens for the first time.
  const welcomeMessage = useMemo(() => ({
    role: 'assistant',
    text: "Hi! I'm iStudyPro's support assistant. Ask me anything about the app — features, billing, or troubleshooting. If your issue needs a human, just say 'talk to human' and I'll connect you.",
    ts: new Date().toISOString(),
  }), []);

  // Allocate a session id once the user first opens the panel.
  useEffect(() => {
    if (!open || sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/support/chat/start`);
        if (cancelled) return;
        setSessionId(data.session_id);
        setPersonalized(!!data.personalized);
        try { localStorage.setItem(SESSION_KEY, data.session_id); } catch {/* ignore */}
        setMessages([welcomeMessage]);
      } catch (err) {
        if (!cancelled) setError("Couldn't start support chat. Please try again later.");
      }
    })();
    return () => { cancelled = true; };
  }, [open, sessionId, welcomeMessage]);

  // Fetch "Common issues this week" chips whenever the panel opens.
  // Refetched per-open so we always reflect the latest telemetry.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/support/chat/suggestions`);
        if (!cancelled) setChips(data.chips || []);
      } catch {
        if (!cancelled) setChips([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Fetch the user's recent chat sessions for the "Saved my last N
  // questions" chip → personal knowledge base. Anonymous users get an
  // empty list back so the chip simply never renders.
  useEffect(() => {
    if (!open || !user) {
      setHistory([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/support/chat/history`);
        if (!cancelled) setHistory(data.sessions || []);
      } catch {
        if (!cancelled) setHistory([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, user]);

  // Auto-scroll to latest message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending, needsEscalation, ticketCreated]);

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || sending || !sessionId) return;
    setInput('');
    setError(null);
    const userMsg = { role: 'user', text, ts: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    setSending(true);
    // Once the user sends a real question, the chips drop away — they
    // were entry-point hints, not a persistent toolbar.
    setChips([]);
    try {
      const { data } = await axios.post(`${API}/support/chat/stream`, {
        session_id: sessionId,
        message: text,
      });
      const replyMsg = {
        role: 'assistant',
        text: data.reply || 'Sorry, I had trouble responding. Try again?',
        ts: new Date().toISOString(),
      };
      setMessages((m) => [...m, replyMsg]);
      if (data.personalized) setPersonalized(true);
      if (data.escalated) setNeedsEscalation(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const submitEscalation = async () => {
    if (!escalationEmail.trim() || !sessionId) return;
    setEscalationSubmitting(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/support/chat/escalate`, {
        session_id: sessionId,
        email: escalationEmail.trim(),
      });
      setTicketCreated(data.ticket_id);
      setNeedsEscalation(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't create your ticket. Try again or email us.");
    } finally {
      setEscalationSubmitting(false);
    }
  };

  const openArchive = async (sid) => {
    setError(null);
    try {
      const { data } = await axios.get(`${API}/support/chat/${sid}`);
      setArchived(data);
      setView('archive');
    } catch (err) {
      setError("Couldn't load that conversation.");
    }
  };

  const backToChat = () => {
    setView('chat');
    setArchived(null);
  };

  return (
    <>
      {/* Trigger */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="support-fab"
            type="button"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.85 }}
            transition={{ type: 'spring', damping: 22, stiffness: 280 }}
            onClick={() => setOpen(true)}
            className="fixed right-5 z-40 w-12 h-12 rounded-full bg-[#002FA7] text-white shadow-xl hover:shadow-2xl hover:scale-105 transition-transform flex items-center justify-center bottom-28 lg:bottom-6"
            title="Talk to Support"
            aria-label="Open support chat"
            data-testid="support-chat-trigger"
          >
            <LifeBuoy className="w-5 h-5" aria-hidden />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="support-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: 'spring', damping: 24, stiffness: 280 }}
            className="fixed right-5 z-40 w-[calc(100vw-2.5rem)] sm:w-96 h-[32rem] bg-[var(--isp-glass)] border border-[var(--isp-glass-border)] shadow-2xl rounded-2xl overflow-hidden flex flex-col bottom-28 lg:bottom-6"
            data-testid="support-chat-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-[#002FA7] text-white">
              <div className="flex items-center gap-2 min-w-0">
                {view === 'chat' && <LifeBuoy className="w-4 h-4 flex-shrink-0" aria-hidden />}
                {(view === 'history' || view === 'archive') && (
                  <button
                    type="button"
                    onClick={view === 'archive' ? () => setView('history') : backToChat}
                    className="p-1 -ml-1 rounded-md hover:bg-white/20"
                    aria-label="Back"
                    data-testid="support-chat-back"
                  >
                    <ChevronLeft className="w-4 h-4" aria-hidden />
                  </button>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-bold leading-tight">
                    {view === 'history' ? 'Recent conversations'
                      : view === 'archive' ? 'Saved conversation'
                      : 'iStudyPro Support'}
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {view === 'chat' && (personalized ? (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-400/20 text-emerald-200 text-[9px] font-bold tracking-wide uppercase"
                        title="The assistant knows your account context — tier, recent lectures, billing — so answers are diagnostic, not generic."
                        data-testid="support-chat-personalized-badge"
                      >
                        <Sparkles className="w-2.5 h-2.5" aria-hidden /> Trained on your usage
                      </span>
                    ) : (
                      <p className="text-[10px] opacity-80 leading-tight">Live AI assistant · Type &quot;talk to human&quot; to escalate</p>
                    ))}
                    {view === 'history' && (
                      <p className="text-[10px] opacity-80 leading-tight">Your last {history.length} support session{history.length === 1 ? '' : 's'}</p>
                    )}
                    {view === 'archive' && archived && (
                      <p className="text-[10px] opacity-80 leading-tight">{(archived.messages || []).length} message{(archived.messages || []).length === 1 ? '' : 's'} · read-only</p>
                    )}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1.5 -mr-1 rounded-md hover:bg-white/20"
                aria-label="Close support chat"
                data-testid="support-chat-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body — switches between live chat, recent-history list,
                and a read-only archive view of a past session. */}
            {view === 'history' && (
              <div className="flex-1 overflow-y-auto px-3 py-4 space-y-2 bg-[var(--isp-page)]" data-testid="support-chat-history">
                {history.length === 0 ? (
                  <p className="text-center text-xs text-[var(--isp-muted)] mt-6">No saved conversations yet.</p>
                ) : (
                  history.map((s) => (
                    <button
                      key={s.session_id}
                      type="button"
                      onClick={() => openArchive(s.session_id)}
                      className="w-full text-left p-3 rounded-xl border border-[var(--isp-glass-border)] bg-[var(--isp-glass-inner)] hover:border-[#002FA7]/40 transition-colors"
                      data-testid={`support-chat-history-item-${s.session_id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold text-[var(--isp-heading)] line-clamp-2">{s.first_question || '(empty conversation)'}</p>
                        {s.escalated && (
                          <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-500/15 text-amber-600 dark:text-amber-300 whitespace-nowrap">TICKET</span>
                        )}
                      </div>
                      {s.last_reply && (
                        <p className="text-[11px] text-[var(--isp-muted)] mt-1 line-clamp-2 italic">{s.last_reply}</p>
                      )}
                      <p className="text-[10px] text-[var(--isp-muted)] mt-1.5">
                        {s.message_count} message{s.message_count === 1 ? '' : 's'} · {new Date(s.updated_at).toLocaleDateString()}
                      </p>
                    </button>
                  ))
                )}
              </div>
            )}

            {view === 'archive' && (
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-[var(--isp-page)]" data-testid="support-chat-archive">
                {(archived?.messages || []).map((m, idx) => (
                  <Bubble key={idx} msg={m} />
                ))}
              </div>
            )}

            {view === 'chat' && (
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-[var(--isp-page)]" data-testid="support-chat-messages">
              {messages.map((m, idx) => (
                <Bubble key={idx} msg={m} />
              ))}
              {sending && (
                <div className="flex items-center gap-2 text-[var(--isp-muted)] text-xs px-2" data-testid="support-chat-typing">
                  <Bot className="w-3.5 h-3.5" aria-hidden />
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '120ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '240ms' }} />
                  </span>
                </div>
              )}
              {needsEscalation && !ticketCreated && (
                <div className="bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] rounded-xl p-3 space-y-2" data-testid="support-chat-escalation">
                  <div className="flex items-center gap-2 text-amber-500">
                    <AlertCircle className="w-4 h-4" aria-hidden />
                    <p className="text-xs font-semibold">Want to talk to a human?</p>
                  </div>
                  <p className="text-[11px] text-[var(--isp-body)] leading-snug">
                    Drop your email and we&apos;ll create a ticket with this conversation. A human agent will reach out within 1 business day.
                  </p>
                  <input
                    type="email"
                    value={escalationEmail}
                    onChange={(e) => setEscalationEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full text-xs px-2.5 py-1.5 rounded-md bg-[var(--isp-page)] border border-[var(--isp-glass-border)] text-[var(--isp-heading)] focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30"
                    data-testid="support-chat-escalation-email"
                  />
                  <button
                    type="button"
                    onClick={submitEscalation}
                    disabled={!escalationEmail.trim() || escalationSubmitting}
                    className="w-full text-xs font-semibold py-1.5 rounded-md bg-[#002FA7] text-white hover:bg-[#00227A] disabled:opacity-50 transition-colors"
                    data-testid="support-chat-escalation-submit"
                  >
                    {escalationSubmitting ? 'Creating ticket…' : 'Create support ticket'}
                  </button>
                </div>
              )}
              {ticketCreated && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 space-y-1" data-testid="support-chat-ticket-created">
                  <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="w-4 h-4" aria-hidden />
                    <p className="text-xs font-bold">Ticket #{ticketCreated} created</p>
                  </div>
                  <p className="text-[11px] text-[var(--isp-body)]">A human agent will follow up at {escalationEmail} within 1 business day.</p>
                </div>
              )}
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-xs text-red-500" data-testid="support-chat-error">
                  {error}
                </div>
              )}
            </div>
            )}

            {/* Chip row — only renders before the user has sent their
                first question. Once they engage, the row clears (so the
                conversation has the whole panel). Hidden when viewing
                history/archive. */}
            {view === 'chat' && (chips.length > 0 || history.length > 0) && (
              <div className="border-t border-[var(--isp-glass-border)] px-3 pt-2.5 pb-1 bg-[var(--isp-glass)]">
                <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-[var(--isp-muted)] mb-1.5">Common issues this week</p>
                <div className="flex flex-wrap gap-1.5" data-testid="support-chat-chips">
                  {history.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setView('history')}
                      disabled={sending}
                      className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/25 transition-colors disabled:opacity-50"
                      data-testid="support-chat-chip-history"
                      title="Browse your past support conversations"
                    >
                      <BookOpen className="w-3 h-3" aria-hidden />
                      <span className="text-[8px] font-bold tracking-wider opacity-80">NEW</span>
                      <span>Saved my last {Math.min(history.length, 3)} question{Math.min(history.length, 3) === 1 ? '' : 's'}</span>
                    </button>
                  )}
                  {chips.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => send(c.question)}
                      disabled={sending}
                      className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-[#002FA7]/8 hover:bg-[#002FA7]/15 text-[#002FA7] border border-[#002FA7]/15 transition-colors disabled:opacity-50"
                      data-testid={`support-chat-chip-${c.id}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input — only in active chat view; history/archive are read-only */}
            {view === 'chat' && (
            <div className="border-t border-[var(--isp-glass-border)] px-3 py-2.5 flex items-center gap-2 bg-[var(--isp-glass)]">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
                placeholder="Type your question…"
                disabled={sending || !sessionId}
                className="flex-1 text-sm px-3 py-2 rounded-lg bg-[var(--isp-page)] border border-[var(--isp-glass-border)] text-[var(--isp-heading)] focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 disabled:opacity-60"
                data-testid="support-chat-input"
              />
              <button
                type="button"
                onClick={() => send()}
                disabled={!input.trim() || sending || !sessionId}
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#002FA7] text-white hover:bg-[#00227A] disabled:opacity-50 transition-colors"
                aria-label="Send message"
                data-testid="support-chat-send"
              >
                <Send className="w-4 h-4" aria-hidden />
              </button>
            </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ReactMarkdown component overrides — defined outside Bubble so React
// can keep referencing the same component types between renders (the
// lint rule `react/no-unstable-nested-components` fires otherwise).
const MD_COMPONENTS = {
  p: (props) => <p className="m-0 mb-2 last:mb-0" {...props} />,
  ul: (props) => <ul className="list-disc list-outside ml-4 my-1 space-y-0.5" {...props} />,
  ol: (props) => <ol className="list-decimal list-outside ml-4 my-1 space-y-0.5" {...props} />,
  li: (props) => <li className="leading-snug" {...props} />,
  strong: (props) => <strong className="font-bold text-[var(--isp-heading)]" {...props} />,
  em: (props) => <em className="italic" {...props} />,
  code: ({ inline, children, ...props }) => inline
    ? <code className="px-1 py-0.5 rounded bg-black/5 dark:bg-white/10 text-[12px] font-mono" {...props}>{children}</code>
    : <pre className="my-1 p-2 rounded bg-black/5 dark:bg-white/10 text-[12px] font-mono overflow-x-auto"><code {...props}>{children}</code></pre>,
  a: (props) => <a className="text-[#002FA7] underline" target="_blank" rel="noopener noreferrer" {...props} />,
};

const Bubble = ({ msg }) => {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex gap-2 ${isUser ? 'flex-row-reverse' : ''}`} data-testid={`support-chat-bubble-${msg.role}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isUser ? 'bg-[#002FA7] text-white' : 'bg-[var(--isp-glass-inner)] text-[var(--isp-heading)]'}`}>
        {isUser ? <User className="w-3.5 h-3.5" aria-hidden /> : <Bot className="w-3.5 h-3.5" aria-hidden />}
      </div>
      <div className={`max-w-[78%] px-3 py-2 rounded-2xl text-sm leading-snug break-words ${
        isUser
          ? 'bg-[#002FA7] text-white rounded-tr-sm whitespace-pre-wrap'
          : 'bg-[var(--isp-glass-inner)] text-[var(--isp-heading)] rounded-tl-sm support-chat-md'
      }`}>
        {isUser ? msg.text : (
          // Assistant replies often contain markdown (bold, lists, code).
          // Without ReactMarkdown the user saw literal **asterisks**.
          // We deliberately do NOT enable raw HTML to keep the surface
          // safe — the LLM has no reason to emit HTML and the plugin
          // would let an attacker (via a prompt-injection vector) sneak
          // arbitrary markup into the chat.
          <ReactMarkdown components={MD_COMPONENTS}>{msg.text}</ReactMarkdown>
        )}
      </div>
    </div>
  );
};

export default SupportChatFab;
